import React, { useState, useRef, useEffect } from 'react'; // ✅ useEffect added
import { X, Check, Plus, FileSpreadsheet, UploadCloud, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { leadsApi, leadDispositionApi,leadAPIApi } from '../services/api'; // ✅ leadDispositionApi imported
import { useTheme } from '../context/ThemeContext';
import { message } from 'antd';

const AddLeadModal = ({ showAddModal, setShowAddModal, onSuccess }) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const [leadType, setLeadType] = useState('single');
  const [newLead, setNewLead] = useState({
    leadSourceRaw: '',
    customerName: '',
    contactNumber: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // ✅ Dispositions states
  const [dispositions, setDispositions] = useState([]);
  const [dispositionsLoading, setDispositionsLoading] = useState(false);

  // Bulk upload states
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const fileInputRef = useRef(null);

  // ✅ Fetch dispositions when modal opens
  useEffect(() => {
    if (showAddModal) {
      fetchDispositions();
    }
  }, [showAddModal]);

  const fetchDispositions = async () => {
    try {
      setDispositionsLoading(true);
      const data = await leadAPIApi.getAllLeadAPIs();
      console.log("data",data)
      // ✅ Adjust based on your API response shape:
      // If response is direct array → setDispositions(data)
      // If response is { data: [...] } → setDispositions(data.data)
      setDispositions(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error('Failed to fetch dispositions:', error);
      message.error('Failed to load lead sources');
    } finally {
      setDispositionsLoading(false);
    }
  };

  // Handle Single Lead Submit
  const handleAddLead = async () => {
    const newErrors = {};
    if (!newLead.customerName.trim()) newErrors.customerName = 'Patient name is required';

    if (!newLead.contactNumber) {
      newErrors.contactNumber = 'Contact number is required';
    } else if (newLead.contactNumber.length !== 10) {
      newErrors.contactNumber = 'Number must be 10 digits';
    } else if (!/^[6-9]\d{9}$/.test(newLead.contactNumber)) {
      newErrors.contactNumber = 'Number must start with 6, 7, 8, or 9';
    }

    if (!newLead.leadSourceRaw) newErrors.leadSourceRaw = 'Lead source is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    try {
      setLoading(true);
      await leadsApi.createLead(newLead);
      message.success('Lead added successfully!');
      setShowAddModal(false);
      setNewLead({ leadSourceRaw: '', customerName: '', contactNumber: '' });
      console.log('Submitting newLead:', newLead);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error adding lead:', error);
      message.error('Failed to add lead');
    } finally {
      setLoading(false);
    }
  };

  // Download Template
  const handleDownloadTemplate = () => {
    const headers = ['customerName', 'contactNumber', 'source', 'email', 'age', 'gender'];
    const sampleRow = ['John Doe', '9876543210', 'F-Lead', 'john@example.com', '30', 'Male'];
    const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'lead_template.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!validTypes.includes(file.type)) {
      message.error('Please upload a valid CSV or Excel file');
      return;
    }

    setUploadFile(file);
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadProgress(progress);
      if (progress >= 100) clearInterval(interval);
    }, 100);
  };

  const handleRemoveFile = () => {
    setUploadFile(null);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleBulkSubmit = async () => {
    if (!uploadFile) {
      message.warning('Please upload a file');
      return;
    }
    try {
      setLoading(true);
      const result = await leadsApi.bulkUploadLeads(uploadFile);
      setUploadResult(result);
      setShowResultModal(true);
      setUploadFile(null);
      setUploadProgress(0);
    } catch (error) {
      console.error('Bulk upload error:', error);
      message.error(`Bulk upload failed: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseResultModal = () => {
    setShowResultModal(false);
    setUploadResult(null);
    setShowAddModal(false);
    setLeadType('single');
    if (onSuccess) onSuccess();
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewLead({ leadSourceRaw: '', customerName: '', contactNumber: '' });
    setErrors({});
    setUploadFile(null);
    setUploadProgress(0);
    setLeadType('single');
    setUploadResult(null);
    setShowResultModal(false);
  };

  const [sourceSearch, setSourceSearch] = useState('');

  if (!showAddModal) return null;

  const filteredDispositions = dispositions.filter(disposition => {
    const label = disposition.leadSource || disposition.name || disposition.title || disposition.dispositionName || '';
    return label.toLowerCase().includes(sourceSearch.toLowerCase());
  });

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm transition-all duration-300 p-4">
        <div className={`rounded-2xl w-full max-w-3xl overflow-hidden shadow-2xl transform transition-all border flex flex-col max-h-[90vh] ${isDark ? 'bg-[#111827] border-white/10' : 'bg-white border-gray-100'}`}>
          {/* Header */}
          <div className="px-6 py-4 flex items-center justify-between border-b flex-shrink-0" style={{ background: currentTheme.color }}>
            <h3 className="text-lg font-bold text-white">Add New Lead</h3>
            <button onClick={handleCloseModal} className="p-1 hover:bg-white/20 rounded-full transition-colors">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          <div className="p-6 space-y-5 overflow-y-auto flex-grow">
            {/* Select Lead Type */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Select Lead type</label>
              <div className="flex gap-4">
                {['single', 'bulk'].map((type) => (
                  <div
                    key={type}
                    onClick={() => setLeadType(type)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all ${leadType !== type ? (isDark ? 'border-white/5 bg-white/5' : 'border-gray-200 bg-white') : ''}`}
                    style={leadType === type ? { borderColor: currentTheme.color, backgroundColor: `${currentTheme.color}${isDark ? '20' : '10'}` } : {}}
                  >
                    <div className="w-4 h-4 rounded-full border flex items-center justify-center"
                      style={{ borderColor: leadType === type ? currentTheme.color : (isDark ? '#374151' : '#9CA3AF') }}>
                      {leadType === type && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: currentTheme.color }} />}
                    </div>
                    <span className={`text-sm font-medium capitalize ${isDark ? (leadType === type ? 'text-white' : 'text-gray-400') : 'text-gray-700'}`}>{type}</span>
                  </div>
                ))}
              </div>
            </div>

            {leadType === 'single' ? (
              <>
                {/* Patient Name & Contact */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Patient name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={newLead.customerName}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                        setNewLead({ ...newLead, customerName: val });
                        if (errors.customerName) setErrors({ ...errors, customerName: null });
                      }}
                      placeholder="Surinder Kannujiya"
                      className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 text-sm transition-all ${isDark ? 'bg-white/5 border-white/10 text-white focus:ring-opacity-20' : 'bg-white border-gray-300 text-gray-900'} ${errors.customerName ? 'border-red-500' : ''}`}
                      onFocus={(e) => e.target.style.borderColor = errors.customerName ? '#EF4444' : currentTheme.color}
                      onBlur={(e) => {
                        e.target.style.borderColor = errors.customerName ? '#EF4444' : (isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB');
                        if (!e.target.value.trim()) setErrors(prev => ({ ...prev, customerName: 'Patient name is required' }));
                      }}
                    />
                    {errors.customerName && <p className="text-red-500 text-xs mt-1 font-medium">{errors.customerName}</p>}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>Contact <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={newLead.contactNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value.length <= 10) {
                          setNewLead({ ...newLead, contactNumber: value });
                          if (value.length > 0 && !/^[6-9]/.test(value)) {
                            setErrors(prev => ({ ...prev, contactNumber: 'Number must start with 6, 7, 8, or 9' }));
                          } else {
                            setErrors(prev => ({ ...prev, contactNumber: null }));
                          }
                        }
                      }}
                      placeholder="7838082254"
                      maxLength="10"
                      className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 text-sm transition-all ${isDark ? 'bg-white/5 border-white/10 text-white focus:ring-opacity-20' : 'bg-white border-gray-300 text-gray-900'} ${errors.contactNumber ? 'border-red-500' : ''}`}
                      onFocus={(e) => e.target.style.borderColor = errors.contactNumber ? '#EF4444' : currentTheme.color}
                      onBlur={(e) => {
                        e.target.style.borderColor = errors.contactNumber ? '#EF4444' : (isDark ? 'rgba(255,255,255,0.1)' : '#D1D5DB');
                        const value = e.target.value;
                        if (!value) setErrors(prev => ({ ...prev, contactNumber: 'Contact number is required' }));
                        else if (!/^[6-9]/.test(value)) setErrors(prev => ({ ...prev, contactNumber: 'Number must start with 6, 7, 8, or 9' }));
                        else if (value.length < 10) setErrors(prev => ({ ...prev, contactNumber: 'Number must be 10 digits' }));
                      }}
                    />
                    {errors.contactNumber && <p className="text-red-500 text-xs mt-1 font-medium">{errors.contactNumber}</p>}
                  </div>
                </div>

                {/* ✅ Lead Source - Now from API */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>
                      Lead source <span className="text-red-500">*</span>
                    </label>
                    {dispositions.length > 8 && (
                      <input
                        type="text"
                        value={sourceSearch}
                        onChange={(e) => setSourceSearch(e.target.value)}
                        placeholder="Filter sources..."
                        className={`text-xs px-2.5 py-1 border rounded-md focus:outline-none ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-800'}`}
                      />
                    )}
                  </div>

                  {dispositionsLoading ? (
                    // ✅ Loading skeleton
                    <div className={`border rounded-xl p-6 flex items-center justify-center ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center gap-2 text-gray-400">
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm">Loading sources...</span>
                      </div>
                    </div>
                  ) : (
                    <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5 border rounded-xl p-4 max-h-72 overflow-y-auto ${isDark ? 'bg-white/5' : 'bg-gray-50/50'} ${errors.leadSourceRaw ? 'border-red-500' : (isDark ? 'border-white/10' : 'border-gray-200')}`}>
                      {filteredDispositions.length === 0 ? (
                        <p className="col-span-full text-center text-sm text-gray-400 py-3">No sources found</p>
                      ) : (
                        filteredDispositions.map((disposition) => {
                          const label = disposition.leadSource || disposition.name || disposition.title || disposition.dispositionName || '';
                          const id = disposition._id || disposition.id || label;
                          const isSelected = newLead.leadSourceRaw === label;

                          return (
                            <div
                              key={id}
                              onClick={() => {
                                setNewLead({ ...newLead, leadSourceRaw: label });
                                if (errors.leadSourceRaw) setErrors({ ...errors, leadSourceRaw: null });
                              }}
                              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border transition-all ${
                                isSelected
                                  ? (isDark ? 'bg-white/10 border-white/20' : 'bg-white shadow-sm')
                                  : (isDark ? 'border-transparent hover:bg-white/5' : 'border-transparent hover:bg-white/80 hover:shadow-sm')
                              }`}
                              style={isSelected ? { borderColor: currentTheme.color } : {}}
                              title={label}
                            >
                              <div
                                className="w-5 h-5 rounded-full border flex items-center justify-center transition-colors shrink-0"
                                style={{
                                  backgroundColor: isSelected ? currentTheme.color : 'transparent',
                                  borderColor: isSelected ? currentTheme.color : (isDark ? '#374151' : '#D1D5DB')
                                }}
                              >
                                {isSelected ? (
                                  <Check className="w-3 h-3 text-white" />
                                ) : (
                                  <Plus className={`w-3 h-3 transition-colors ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                )}
                              </div>
                              <span className={`text-xs truncate font-medium transition-colors ${isSelected ? (isDark ? 'text-white font-semibold' : 'text-gray-900 font-semibold') : (isDark ? 'text-gray-400' : 'text-gray-700')}`}>
                                {label}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                  {errors.leadSourceRaw && <p className="text-red-500 text-xs mt-1">{errors.leadSourceRaw}</p>}
                </div>

                {/* Footer */}
                <div className="flex justify-end pt-2">
                  <button
                    onClick={handleAddLead}
                    className={`px-8 py-2.5 font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-lg active:scale-95 disabled:opacity-50 disabled:grayscale disabled:scale-100 ${loading ? (isDark ? 'bg-white/10 text-gray-500' : 'bg-gray-200 text-gray-400 cursor-not-allowed') : 'text-white'}`}
                    style={loading ? {} : { backgroundColor: currentTheme.color }}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Okay'}
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-6 animate-in fade-in duration-300">
                {/* Step 1: Download */}
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-sm shrink-0 mt-0.5 ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}>1</div>
                    <div>
                      <h4 className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Please download Excel file template.</h4>
                      <p className="text-sm text-gray-500 mt-1">Choose and download any template and fill in your members data.</p>
                    </div>
                  </div>
                  <div className="pl-9">
                    <button
                      onClick={handleDownloadTemplate}
                      className="flex items-center gap-2 px-4 py-2 border rounded-lg transition-all active:scale-95 shadow-sm"
                      style={{ borderColor: currentTheme.color, color: currentTheme.color, backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'white' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.1)' : `${currentTheme.color}10`}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.05)' : 'white'}
                    >
                      <FileSpreadsheet className="w-5 h-5" />
                      <span className="font-black text-xs uppercase tracking-widest">Download</span>
                    </button>
                  </div>
                </div>

                {/* Step 2: Upload */}
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-sm shrink-0 mt-0.5 ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}>2</div>
                    <div>
                      <h4 className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Please upload updated Excel file template.</h4>
                      <p className="text-sm text-gray-500 mt-1">Please upload filled data here.</p>
                    </div>
                  </div>
                  <div className="pl-9">
                    <div
                      onClick={!uploadFile ? handleUploadClick : undefined}
                      className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center gap-3 transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-300'} ${!uploadFile ? (isDark ? 'hover:bg-white/10 hover:border-white/10 cursor-pointer group' : 'hover:bg-gray-100 cursor-pointer group') : ''}`}
                    >
                      {uploadFile ? (
                        <div className={`w-full rounded-xl border p-4 shadow-sm animate-in fade-in zoom-in-95 duration-200 ${isDark ? 'bg-white/10 border-white/10' : 'bg-white border-gray-100'}`}>
                          <div className="flex justify-between items-start mb-3">
                            <span className="text-sm font-medium text-gray-800 truncate pr-4">{uploadFile.name}</span>
                            <button onClick={handleRemoveFile} className="text-gray-400 hover:text-gray-600 transition-colors">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-300 ease-out"
                              style={{ width: `${uploadProgress}%`, backgroundColor: currentTheme.color }}
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <UploadCloud className="w-8 h-8 text-gray-400 group-hover:text-emerald-500 transition-colors" />
                          <button className={`px-4 py-1.5 border rounded-lg text-xs font-black uppercase tracking-widest transition-all active:scale-95 ${isDark ? 'bg-white/10 border-white/10' : 'bg-white border-gray-200'}`}
                            style={{ borderColor: currentTheme.color, color: currentTheme.color }}>
                            Click to Upload
                          </button>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            className="hidden"
                            accept=".csv,.xlsx,.xls"
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end pt-4">
                  <button
                    onClick={handleBulkSubmit}
                    className={`px-8 py-2.5 font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-lg active:scale-95 disabled:opacity-50 disabled:grayscale disabled:scale-100 ${!uploadFile || loading ? (isDark ? 'bg-white/10 text-gray-500' : 'bg-gray-200 text-gray-400 cursor-not-allowed') : 'text-white'}`}
                    style={(!uploadFile || loading) ? {} : { backgroundColor: currentTheme.color }}
                    disabled={!uploadFile || loading}
                  >
                    {loading ? 'Uploading...' : 'Okay'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Result Modal */}
      {showResultModal && uploadResult && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] backdrop-blur-sm transition-all duration-300">
          <div className={`rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl transform transition-all animate-in zoom-in-95 duration-200 border ${isDark ? 'bg-[#111827] border-white/10' : 'bg-white border-gray-100'}`}>
            <div className={`px-6 py-4 flex items-center justify-between border-b ${uploadResult.success ? (isDark ? 'bg-green-500/5 border-green-500/10' : 'bg-green-50 border-green-100') : (isDark ? 'bg-red-500/5 border-red-500/10' : 'bg-red-50 border-red-100')}`}>
              <div className="flex items-center gap-3">
                {uploadResult.success ? <CheckCircle className="w-6 h-6 text-green-600" /> : <AlertCircle className="w-6 h-6 text-red-600" />}
                <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{uploadResult.success ? 'Upload Complete' : 'Upload Failed'}</h3>
              </div>
              <button onClick={handleCloseResultModal} className="p-1 hover:bg-black/10 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className={`border rounded-xl p-4 text-center ${isDark ? 'bg-green-500/5 border-green-500/10' : 'bg-green-50 border-green-200'}`}>
                  <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-700'}`}>{uploadResult.successCount || 0}</p>
                  <p className={`text-xs font-medium mt-1 ${isDark ? 'text-green-500/70' : 'text-green-600'}`}>Success</p>
                </div>
                <div className={`border rounded-xl p-4 text-center ${isDark ? 'bg-yellow-500/5 border-yellow-500/10' : 'bg-yellow-50 border-yellow-200'}`}>
                  <AlertCircle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                  <p className={`text-2xl font-bold ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>{uploadResult.duplicateCount || 0}</p>
                  <p className={`text-xs font-medium mt-1 ${isDark ? 'text-yellow-500/70' : 'text-yellow-600'}`}>Duplicates</p>
                </div>
                <div className={`border rounded-xl p-4 text-center ${isDark ? 'bg-red-500/5 border-red-500/10' : 'bg-red-50 border-red-200'}`}>
                  <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                  <p className={`text-2xl font-bold ${isDark ? 'text-red-400' : 'text-red-700'}`}>{uploadResult.failureCount || 0}</p>
                  <p className={`text-xs font-medium mt-1 ${isDark ? 'text-red-500/70' : 'text-red-600'}`}>Failed</p>
                </div>
              </div>
              {uploadResult.message && (
                <div className={`border rounded-lg p-4 ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                  <p className={`text-sm text-center ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{uploadResult.message}</p>
                </div>
              )}
            </div>

            <div className={`px-6 py-4 border-t flex justify-end ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
              <button
                onClick={handleCloseResultModal}
                className="px-8 py-2.5 text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-lg active:scale-95"
                style={{ backgroundColor: currentTheme.color }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddLeadModal;